import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { TableActionTypes, TableState, useSnackbarHandler } from 'web-library';
import { useDeleteBlacklistedCandidate } from '../../../api-refactored/candidate/useDeleteBlacklistedCandidate';
import { usePostBlacklistedCandidateSearch } from '../../../api-refactored/candidate/usePostBlacklistedCandidateSearch';
import { UuidModel } from '../../../api-refactored/typings';
import { getIndexValue } from '../../../utils/ArrayUtils';
import { fallbackNull } from '../../../utils/ComponentUtils';
import { getPageNumAfterRemovingItem } from '../../../utils/PageUtils';

export interface SelectedCandidate {
  uuid: string;
  name: string;
}

export interface UseCandidateBlacklistingReturn {
  /**
   * Blacklisted candidate search mutation's loading status.
   */
  isSearchingCandidate: boolean;
  /**
   * Trigger Blacklisted candidate search mutation.
   */
  triggerCandidateSearch: () => void;
  /**
   * Blacklisted candidate delete mutation's status.
   */
  candidateDeletionStatus: MutationStatus;
  /**
   * Method to handle action after delete blacklisted candidate confirmation modal closed.
   */
  handleDeleteConfirmationModalClose: () => void;
  /**
   * Method to handle action after delete blacklisted candidate confirmation modal opened.
   */
  handleDeleteConfirmationModalOpen: () => void;
  /**
   * Method to handle action after user confirm the deletion of blacklisted candidate.
   */
  onDeleteModalConfirmed: () => void;
  /**
   * Method to handle action after remove selected button clicked.
   */
  onRemoveSelectedClicked: () => void;
  /**
   * Indicator whether delete confirmation modal is opened.
   */
  openConfirmationModal: boolean;
  /**
   * Selected candidate to be deleted.
   */
  selectedCandidate: SelectedCandidate[];
  /**
   * Set selected candidate to be deleted.
   */
  setSelectedCandidate: Dispatch<SetStateAction<SelectedCandidate[]>>;
  /**
   * Indicator whether Bulk upload modal is opened.
   */
  isShowModalBulkUpload: boolean;
  /**
   * Method to handle action close bulk upload modal.
   */
  handleCloseBulkUploadModal: () => void;
  /**
   * Method to handle action open bulk upload modal.
   */
  handleShowBulkUploadModal: () => void;
  /**
   * Method to handle action download candidate blacklist template.
   */
  numberOfRecords: number;
  setNumberOfRecords: (record: number) => void;
}

export const useCandidateBlacklisting = ({
  tableDispatch,
  state: tableState,
}: {
  tableDispatch: Dispatch<TableActionTypes>;
  state: TableState;
}): UseCandidateBlacklistingReturn => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();

  const [selectedCandidate, setSelectedCandidate] = useState<SelectedCandidate[]>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [isShowModalBulkUpload, setIsShowModalBulkUpload] = useState<boolean>(false);
  const [numberOfRecords, setNumberOfRecords] = useState<number>(0);

  const candidateSearchMutation = usePostBlacklistedCandidateSearch({
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.content,
            count: data.totalElements,
          },
        } as TableActionTypes);
      }
    },
  });

  const triggerCandidateSearch = () => {
    candidateSearchMutation.mutate({
      searchParam: fallbackNull(tableState.search),
      examType: fallbackNull(getIndexValue(tableState.filters.examType, 0)),
      pathParams: {
        page: tableState.page,
        pageSize: tableState.size,
        order: tableState.order,
        sort: tableState.sortColumn,
      },
    });
  };

  const reloadTableDataAfterDeletion = (removedItemNum: number) => {
    const targetPage = getPageNumAfterRemovingItem(
      tableState.count,
      removedItemNum,
      tableState.size,
      tableState.page
    );

    if (targetPage !== tableState.page) {
      tableDispatch({
        type: 'UPDATE_PAGE',
        payload: targetPage,
      });
    } else {
      triggerCandidateSearch();
    }
  };

  const deleteCandidateMutate = useDeleteBlacklistedCandidate({
    onSuccess: (res, reqData) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>CandidateBlacklist.HasBeenRemoved', {
          numberOfCandidate: selectedCandidate.length.toString(),
        }),
      });
      tableDispatch({
        type: 'CLEAR_SELECTION',
      });
      reloadTableDataAfterDeletion(reqData.length);
    },
    onError: () => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('admin->>CandidateBlacklist.UnableToRemove', {
          numberOfCandidate: selectedCandidate.length.toString(),
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    },
    onSettled: () => {
      handleDeleteConfirmationModalClose();
      deleteCandidateMutate.reset();
    },
  });

  const handleDeleteConfirmationModalClose = () => {
    setOpenConfirmationModal(false);
    setSelectedCandidate([]);
  };

  const handleDeleteConfirmationModalOpen = () => {
    setOpenConfirmationModal(true);
  };

  const handleCloseBulkUploadModal = () => {
    setIsShowModalBulkUpload(false);
  };

  const handleShowBulkUploadModal = () => {
    setIsShowModalBulkUpload(true);
  };

  const onDeleteModalConfirmed = () => {
    const selectedList: UuidModel[] = selectedCandidate.map((s) => ({
      uuid: s.uuid,
    }));
    deleteCandidateMutate.mutate(selectedList);
  };

  const onRemoveSelectedClicked = () => {
    const selectedList: SelectedCandidate[] = Object.keys(tableState.rowsSelected).map((s) => {
      const targetCandidate = tableState.rowsSelected[s];
      return {
        uuid: targetCandidate.uuid,
        name: targetCandidate.name,
      };
    });
    setSelectedCandidate(selectedList);
    handleDeleteConfirmationModalOpen();
  };

  return {
    triggerCandidateSearch,
    candidateDeletionStatus: deleteCandidateMutate.status,
    isSearchingCandidate: candidateSearchMutation.isLoading,
    handleDeleteConfirmationModalClose,
    handleDeleteConfirmationModalOpen,
    onDeleteModalConfirmed,
    onRemoveSelectedClicked,
    openConfirmationModal,
    selectedCandidate,
    setSelectedCandidate,
    isShowModalBulkUpload,
    handleCloseBulkUploadModal,
    handleShowBulkUploadModal,
    numberOfRecords,
    setNumberOfRecords,
  };
};
